import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Bell, AlertTriangle, Trash2, Check, CheckCheck, Reply, Send } from 'lucide-react';
import { toast } from 'sonner';
import useMessageStream from '../hooks/useMessageStream';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Bell icon with unread badge for authenticated employees.
 * Click → dropdown listing their inbox (mark read/unread, delete).
 */
export const EmployeeMessagesBell = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employee/messages/unread-count`, { withCredentials: true });
      setUnread(res.data.unread || 0);
    } catch {/* ignore */}
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/employee/messages`, { withCredentials: true });
      setMessages(res.data || []);
    } catch {
      toast.error('Failed to load messages');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 15000);
    const onFocus = () => fetchUnread();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchUnread]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  // Real-time: refresh unread count (and dropdown list when open) on each SSE event.
  useMessageStream(useCallback(() => {
    fetchUnread();
    if (open) fetchList();
    // Subtle heads-up when a message arrives while the bell is closed
    if (!open) {
      try { toast('New message', { description: 'Open your inbox to read.' }); } catch {/* ignore */}
    }
  }, [fetchUnread, fetchList, open]));

  const markRead = async (id) => {
    await axios.post(`${API_URL}/api/employee/messages/${id}/read`, {}, { withCredentials: true });
    fetchList(); fetchUnread();
  };
  const markUnread = async (id) => {
    await axios.post(`${API_URL}/api/employee/messages/${id}/unread`, {}, { withCredentials: true });
    fetchList(); fetchUnread();
  };
  const del = async (id) => {
    await axios.delete(`${API_URL}/api/employee/messages/${id}`, { withCredentials: true });
    fetchList(); fetchUnread();
  };

  const [replyOpen, setReplyOpen] = useState(null); // message id being replied to
  const [replyText, setReplyText] = useState('');
  const sendEmployeeReply = async (msg) => {
    if (!replyText.trim()) return;
    try {
      await axios.post(`${API_URL}/api/employee/messages/reply`,
        { body: replyText.trim(), reply_to_id: msg.message_id },
        { withCredentials: true });
      toast.success('Reply sent');
      setReplyOpen(null); setReplyText('');
      fetchList();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative rounded-full border border-[#E8E5DF] w-10 h-10 flex items-center justify-center hover:border-[#FF3B6B] hover:text-[#FF3B6B] transition-colors bg-white"
          aria-label="Employee messages"
          data-testid="employee-messages-bell"
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 bg-[#FF3B6B] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center"
              data-testid="employee-messages-unread-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 bg-white p-0 max-h-[70vh] overflow-y-auto">
        <div className="p-3 border-b border-[#E8E5DF] flex items-center justify-between">
          <p className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Messages</p>
          <span className="text-xs text-[#6B6B7A]">{unread} unread</span>
        </div>
        {loading && <p className="p-4 text-sm text-[#6B6B7A]">Loading…</p>}
        {!loading && messages.length === 0 && (
          <p className="p-6 text-sm text-[#6B6B7A] text-center">No messages yet.</p>
        )}
        {!loading && messages.map((m) => (
          <div
            key={m.message_id}
            className={`p-3 border-b border-[#E8E5DF] ${!m.is_read ? 'bg-[#FF3B6B]/5' : 'bg-white'}`}
            data-testid={`msg-item-${m.message_id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {m.high_priority && <AlertTriangle className="w-3 h-3 text-red-500" />}
                  <p className={`text-sm font-medium truncate ${!m.is_read ? 'text-[#1E1E24]' : 'text-[#6B6B7A]'}`}>
                    {m.subject}
                  </p>
                </div>
                <p className="text-xs text-[#9a9aa6] mt-0.5">
                  {m.sender_name} · {new Date(m.created_at).toLocaleString()}
                </p>
                <p
                  className="text-xs text-[#1E1E24] mt-1 whitespace-pre-wrap cursor-pointer"
                  onClick={() => {
                    setExpanded(prev => ({ ...prev, [m.message_id]: !prev[m.message_id] }));
                    if (!m.is_read) markRead(m.message_id);
                  }}
                >
                  {expanded[m.message_id] ? m.body : (m.body.length > 80 ? `${m.body.slice(0, 80)}…` : m.body)}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                {m.is_read ? (
                  <button onClick={() => markUnread(m.message_id)} title="Mark unread" data-testid={`msg-${m.message_id}-unread`}
                    className="w-7 h-7 rounded-full hover:bg-[#FFFDF8] text-[#6B6B7A] flex items-center justify-center">
                    <Check className="w-3 h-3" />
                  </button>
                ) : (
                  <button onClick={() => markRead(m.message_id)} title="Mark read" data-testid={`msg-${m.message_id}-read`}
                    className="w-7 h-7 rounded-full hover:bg-[#FFFDF8] text-[#06D6A0] flex items-center justify-center">
                    <CheckCheck className="w-3 h-3" />
                  </button>
                )}
                <button onClick={() => { setReplyOpen(m.message_id); setReplyText(''); }} title="Reply" data-testid={`msg-${m.message_id}-reply`}
                  className="w-7 h-7 rounded-full hover:bg-[#FFFDF8] text-[#FF3B6B] flex items-center justify-center">
                  <Reply className="w-3 h-3" />
                </button>
                <button onClick={() => del(m.message_id)} title="Delete" data-testid={`msg-${m.message_id}-delete`}
                  className="w-7 h-7 rounded-full hover:bg-red-50 text-red-500 flex items-center justify-center">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
            {replyOpen === m.message_id && (
              <div className="mt-2 flex gap-2">
                <input
                  autoFocus
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendEmployeeReply(m); }}
                  placeholder="Type a reply…"
                  maxLength={2000}
                  className="flex-1 text-xs rounded-full border border-[#E8E5DF] px-3 py-1"
                  data-testid={`reply-input-${m.message_id}`}
                />
                <button
                  onClick={() => sendEmployeeReply(m)}
                  className="w-7 h-7 rounded-full bg-[#FF3B6B] text-white flex items-center justify-center disabled:opacity-50"
                  disabled={!replyText.trim()}
                  data-testid={`send-reply-${m.message_id}`}
                >
                  <Send className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default EmployeeMessagesBell;
